import { useState, useEffect, useRef, useCallback, type ReactNode, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import { Search as SearchIcon, File, X } from 'lucide-react'
import { useFileSystem, useWorkspace } from '../hooks/useWorkspace'
import './SearchPanel.css'

interface SearchMatch {
    lineNumber: number
    text: string
}

interface SearchGroup {
    path: string
    name: string
    matches: SearchMatch[]
}

export interface SearchPanelProps {
    /** Called when a search result is clicked. `line` is the 1-based line number (§40 §8.1). */
    onSelect: (path: string, line?: number) => void
    onClose: () => void
}

/**
 * Full-text search across all files (Cmd+Shift+F).
 * Loads file contents asynchronously for search, groups results by file.
 */
export default function SearchPanel({ onSelect, onClose }: SearchPanelProps) {
    const ws = useWorkspace()
    const { files } = useFileSystem()
    const [query, setQuery] = useState('')
    const [results, setResults] = useState<SearchGroup[]>([])
    const [searching, setSearching] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)
    const searchIdRef = useRef(0)

    useEffect(() => {
        inputRef.current?.focus()
    }, [])

    // Debounced async search
    useEffect(() => {
        const searchId = ++searchIdRef.current
        let cancelled = false

        if (!query.trim() || query.length < 2) {
            setResults([])
            setSearching(false)
            return () => {
                cancelled = true
            }
        }

        setSearching(true)

        const timer = setTimeout(async () => {
            const q = query.toLowerCase()
            const grouped: SearchGroup[] = []

            // Load content for each file and search
            for (const f of files) {
                if (cancelled || searchId !== searchIdRef.current) return

                let content: string | null
                try {
                    content = await ws.fs.readText(f.path)
                } catch {
                    if (cancelled || searchId !== searchIdRef.current) return

                    // Skip files that can't be read (e.g., deleted between list and read,
                    // or pipeline errors on encrypted content)
                    continue
                }
                if (cancelled || searchId !== searchIdRef.current) return
                if (!content) continue

                const lines = content.split('\n')
                const matches: SearchMatch[] = []
                for (let i = 0; i < lines.length; i++) {
                    if (lines[i].toLowerCase().includes(q)) {
                        matches.push({ lineNumber: i + 1, text: lines[i].trim() })
                        if (matches.length >= 5) break
                    }
                }
                if (matches.length > 0) {
                    grouped.push({ path: f.path, name: f.path.split('/').pop() ?? f.path, matches })
                }
            }

            if (cancelled || searchId !== searchIdRef.current) return
            setResults(grouped)
            setSearching(false)
        }, 200)

        return () => {
            cancelled = true
            clearTimeout(timer)
        }
    }, [query, files, ws])

    const totalMatches = results.reduce((sum, g) => sum + g.matches.length, 0)

    const handleKeyDown = useCallback((e: ReactKeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape') onClose()
    }, [onClose])

    return (
        <div className="search-backdrop" onClick={onClose}>
            <div className="search-modal" onClick={e => e.stopPropagation()} role="dialog">
                <div className="search-header">
                    <div className="search-input-row">
                        <SearchIcon size={16} className="search-icon" />
                        <input
                            ref={inputRef}
                            className="search-input"
                            type="text"
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Search all files…"
                            autoComplete="off"
                            spellCheck="false"
                        />
                        <button className="btn btn-icon btn-sm" onClick={onClose}>
                            <X size={16} />
                        </button>
                    </div>
                    {query.length >= 2 && (
                        <div className="search-summary">
                            {searching
                                ? 'Searching…'
                                : `${totalMatches} match${totalMatches !== 1 ? 'es' : ''} in ${results.length} file${results.length !== 1 ? 's' : ''}`
                            }
                        </div>
                    )}
                </div>

                <div className="search-results">
                    {query.length >= 2 && !searching && results.length === 0 && (
                        <div className="search-empty">No matches for &quot;{query}&quot;</div>
                    )}
                    {results.map(group => (
                        <div key={group.path} className="search-group">
                            <div className="search-group-header">
                                <File size={14} />
                                <span className="search-group-name">{group.name}</span>
                                <span className="search-group-path">{group.path}</span>
                            </div>
                            {group.matches.map((m, i) => (
                                <button
                                    key={i}
                                    className="search-match"
                                    onClick={() => { onSelect(group.path, m.lineNumber); onClose() }}
                                >
                                    <span className="search-line-num">{m.lineNumber}</span>
                                    <span className="search-line-text">
                                        {highlightMatch(m.text, query)}
                                    </span>
                                </button>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

function highlightMatch(text: string, query: string): ReactNode {
    const idx = text.toLowerCase().indexOf(query.toLowerCase())
    if (idx === -1) return text
    const start = Math.max(0, idx - 40)
    const end = Math.min(text.length, idx + query.length + 40)
    const snippet = (start > 0 ? '…' : '') + text.slice(start, end) + (end < text.length ? '…' : '')
    const matchStart = idx - start + (start > 0 ? 1 : 0)
    return (
        <>
            {snippet.slice(0, matchStart)}
            <mark>{snippet.slice(matchStart, matchStart + query.length)}</mark>
            {snippet.slice(matchStart + query.length)}
        </>
    )
}
